import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

export function stripIsoFormatting(value: string): string {
  return value
    .replace('T', ' ')
    .replace(/\.\d+/, '')
    .replace(/(Z|[+-]\d{2}:?\d{2})$/, '');
}

// Given the API's ISO-ish wall-clock + the store's IANA timezone, return the
// true UTC instant. The API's trailing offset suffix (often "+00:00") doesn't
// match the wall-clock, so we strip it and interpret the wall-clock as
// store-local. dayjs.tz handles DST correctly.
export function storeWallClockToUtcInstant(value: string, timeZone: string): Date | null {
  const wallClock = value.replace(/(Z|[+-]\d{2}:?\d{2})$/, '');
  const d = dayjs.tz(wallClock, timeZone);
  return d.isValid() ? d.toDate() : null;
}

function getOffsetLabel(instant: Date, timeZone?: string): string {
  if (isNaN(instant.getTime())) return 'UTC';
  const parts = new Intl.DateTimeFormat('en-CA', {
    ...(timeZone ? { timeZone } : {}),
    timeZoneName: 'shortOffset',
  }).formatToParts(instant);
  const raw = parts.find((p) => p.type === 'timeZoneName')?.value;
  return raw ? raw.replace('GMT', 'UTC') : 'UTC';
}

// Format an instant using the user's default locale, in the given timezone
// (or the browser's default zone if omitted), appending "(UTC±N)".
export function formatInstantInTimeZone(instant: Date, timeZone?: string): string {
  if (isNaN(instant.getTime())) return '';
  const formatted = new Intl.DateTimeFormat(undefined, {
    ...(timeZone ? { timeZone } : {}),
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  }).format(instant);
  return `${formatted} (${getOffsetLabel(instant, timeZone)})`;
}

export function formatInStoreTimeZone(value: string, timeZone: string | undefined): string {
  if (!value) return '';
  if (!timeZone) return stripIsoFormatting(value);
  const instant = storeWallClockToUtcInstant(value, timeZone);
  if (!instant) return stripIsoFormatting(value);
  return formatInstantInTimeZone(instant, timeZone);
}
